import {
  createDateControl,
} from "./dateControl.js";
import {
  createSelectControl,
} from "./selectControl.js";
import {
  createConfigOptions,
  createDomainOptions,
  createStreamScopedConfigOptions,
  filterOptionsByStream,
  optionSupportsStream,
  createUserOptions,
} from "./controlOptions.js";
import {
  getScopedTypeOptions,
} from "../../utils/typeScope.js";

const STATUS_CONFIG_KEYS = {
  patient: "patientStatus",
  patients: "patientStatus",
  lab: "labStatus",
  labs: "labStatus",
  project: "projectStatus",
  projects: "projectStatus",
  asset: "equipmentStatus",
  assets: "equipmentStatus",
  maintenance: "maintenanceStatus",
  task: "taskStatus",
  tasks: "taskStatus",
};

const TYPE_CONFIG_KEYS = {
  patient: "patientTypes",
  patients: "patientTypes",
  lab: "labTypes",
  labs: "labTypes",
  project: "projectTypes",
  projects: "projectTypes",
  asset: "assetTypes",
  assets: "assetTypes",
  maintenance: "maintenanceTypes",
  task: "taskTypes",
  tasks: "taskTypes",
};
const DEFAULT_SEARCH_THRESHOLD = 7;

function getConfig(rootData) {
  return rootData?.config || rootData || {};
}

function getUsers(rootData, users) {
  if (Array.isArray(users)) {return users;}
  return Array.isArray(rootData?.users) ? rootData.users : [];
}

function getSelectedValues(value, multiple = false) {
  if (multiple) {
    return Array.isArray(value) ? value : [value].filter(Boolean);
  }
  return value ? [value] : [];
}

function filterUsersByPermission(users, collectionName) {
  if (!collectionName) {return users;}
  return users.filter((user) => {
    return user?.role === "admin" ||
      Array.isArray(user?.permissions) && user.permissions.includes(collectionName);
  });
}

function userHasCollectionPermission(user, collectionName) {
  if (!collectionName) {return true;}
  return filterUsersByPermission([user], collectionName).length > 0;
}

function getCollectionAccessLabel(collectionName) {
  return collectionName ? "no access" : "not selectable";
}

function keepSelectedUsers(users, selectedValues, filterFn) {
  const selectedSet = new Set(selectedValues.map((value) => String(value)));
  return users.filter((user) => {
    if (!user?.id) {return false;}
    return filterFn(user) || selectedSet.has(String(user.id));
  });
}

function buildSelectConfig(config, options) {
  return {
    ...config,
    options,
    searchable: config.searchable ?? options.length >= DEFAULT_SEARCH_THRESHOLD,
    searchThreshold: config.searchThreshold ?? DEFAULT_SEARCH_THRESHOLD,
    clearable: config.clearable ?? true,
  };
}

/**
 * Creates a status select preset for an entity type using config-backed options.
 * @param {object} config Preset configuration.
 * @param {HTMLElement} config.root Root element to own the rendered control.
 * @param {object} config.appData Runtime app data containing config.
 * @param {string} config.itemType Entity type, such as task or patient.
 * @param {string} [config.name] Field name override.
 * @param {string} [config.value] Selected status id.
 * @returns {object} Select lifecycle handle.
 */
export function createStatusSelectPreset(config) {
  const configKey = config.configKey || STATUS_CONFIG_KEYS[config.itemType];
  const selectedValues = getSelectedValues(config.value);
  const optionConfig = {
    selectedValues,
    stream: config.stream,
  };
  const options = config.stream ?
    createStreamScopedConfigOptions(getConfig(config.appData), configKey, optionConfig) :
    createConfigOptions(getConfig(config.appData), configKey, optionConfig);

  return createSelectControl(buildSelectConfig({
    placeholder: "+ Add status",
    ...config,
    name: config.name || (config.itemType === "asset" || config.itemType === "assets" ?
      "equipment_status" :
      "status"),
    searchable: false,
  }, options));
}

/**
 * Creates a stream-filtered status select preset for Labs/Maintenance.
 * @param {object} config Preset configuration.
 * @param {HTMLElement} config.root Root element to own the rendered control.
 * @param {object} config.appData Runtime app data containing config.
 * @param {string} config.itemType Entity type, such as lab or maintenance.
 * @param {string} config.stream Immutable item stream.
 * @param {string} [config.value] Selected status id.
 * @returns {object} Select lifecycle handle.
 */
export function createStreamScopedStatusSelectPreset(config) {
  return createStatusSelectPreset(config);
}

/**
 * Creates a priority select preset using config.priorities.
 * @param {object} config Preset configuration.
 * @param {HTMLElement} config.root Root element to own the rendered control.
 * @param {object} config.appData Runtime app data containing config.
 * @param {string} [config.value] Selected priority id.
 * @returns {object} Select lifecycle handle.
 */
export function createPrioritySelectPreset(config) {
  const options = createConfigOptions(getConfig(config.appData), "priorities", {
    selectedValues: getSelectedValues(config.value),
  });

  return createSelectControl(buildSelectConfig({
    placeholder: "+ Add urgency",
    ...config,
    name: config.name || "priority",
  }, options));
}

/**
 * Creates a permission-aware user select preset.
 * @param {object} config Preset configuration.
 * @param {HTMLElement} config.root Root element to own the rendered control.
 * @param {object} [config.appData] Runtime app data containing users.
 * @param {Array<object>} [config.users] Explicit user list override.
 * @param {string} [config.collectionName] Optional permission collection filter.
 * @param {string|Array<string>} [config.value] Selected user id or ids.
 * @returns {object} Select lifecycle handle.
 */
export function createUserSelectPreset(config) {
  const multiple = Boolean(config.multiple);
  const selectedValues = getSelectedValues(config.value, multiple);
  const users = keepSelectedUsers(
    getUsers(config.appData, config.users),
    selectedValues,
    (user) => filterUsersByPermission([user], config.collectionName).length > 0,
  );
  const options = createUserOptions(users, {
    selectedValues,
    includeInactive: config.includeInactive,
    labelResolver: config.labelResolver,
  }).map((option) => {
    const selectedOnly = selectedValues.includes(String(option.value)) &&
      !userHasCollectionPermission(option.source, config.collectionName);
    return selectedOnly ?
      {
        ...option,
        selectedOnly: true,
        unavailableLabel: getCollectionAccessLabel(config.collectionName),
      } :
      option;
  });

  return createSelectControl(buildSelectConfig({
    placeholder: multiple ? "+ Add users" : "+ Add user",
    ...config,
    multiple,
  }, options));
}

/**
 * Creates a provider select preset backed by app provider users.
 * @param {object} config Preset configuration.
 * @param {HTMLElement} config.root Root element to own the rendered control.
 * @param {object} [config.appData] Runtime app data containing users.
 * @param {string|Array<string>} [config.value] Selected provider id or ids.
 * @returns {object} Select lifecycle handle.
 */
export function createProviderSelectPreset(config) {
  const multiple = config.multiple ?? true;
  const selectedValues = getSelectedValues(config.value, multiple);
  const users = keepSelectedUsers(
    getUsers(config.appData, config.users),
    selectedValues,
    (user) => user?.isAppProvider === true,
  );
  const options = createUserOptions(users, {
    selectedValues,
    providersOnly: true,
    includeInactive: config.includeInactive,
    labelResolver: config.labelResolver,
  });

  return createSelectControl(buildSelectConfig({
    placeholder: multiple ? "+ Add providers" : "+ Add provider",
    ...config,
    name: config.name || "provider",
    multiple,
  }, options));
}

/**
 * Creates a type select preset for an entity type using config-backed options.
 * @param {object} config Preset configuration.
 * @param {HTMLElement} config.root Root element to own the rendered control.
 * @param {object} config.appData Runtime app data containing config.
 * @param {string} config.itemType Entity type, such as task or patient.
 * @param {Array<object>} [config.options] Explicit type option list override.
 * @param {string|Array<string>} [config.value] Selected type id or ids.
 * @param {Function} [config.optionFilter] Optional permission-aware type filter.
 * @param {boolean} [config.scopeTypes=true] Whether to apply team-scoped type visibility.
 * @param {object} [config.scopeUser] User record for scoped type visibility.
 * @returns {object} Select lifecycle handle.
 */
export function createTypeSelectPreset(config) {
  const multiple = config.multiple ?? true;
  const configKey = config.configKey || TYPE_CONFIG_KEYS[config.itemType];
  const sourceOptions = Array.isArray(config.options) ?
    config.options :
    getConfig(config.appData)?.[configKey] || [];
  const selectedValues = getSelectedValues(config.value, multiple);
  const selectedSet = new Set(selectedValues.map((value) => String(value)));
  const streamScopedSourceOptions = filterOptionsByStream(sourceOptions, {
    selectedValues,
    stream: config.stream,
  });
  const rawOptions = config.scopeTypes === false ?
    streamScopedSourceOptions :
    getScopedTypeOptions(
      streamScopedSourceOptions,
      selectedValues,
      config.scopeUser,
    );
  const filteredOptions = typeof config.optionFilter === "function" ?
    rawOptions.filter((option) => config.optionFilter(option) || selectedSet.has(String(option?.id))) :
    rawOptions;
  const options = createDomainOptions(filteredOptions, {
    selectedValues,
    archivedResolver: (option) => option?.isActive === false,
  }).map((option) => {
    const isSelected = selectedSet.has(String(option.value));
    const isStreamMismatch = !optionSupportsStream(option.source, config.stream);
    const isSelectedOnly = isSelected && (
      option.source?.isAddable === false ||
      isStreamMismatch
    );
    if (!isSelectedOnly) {return option;}
    return {
      ...option,
      selectedOnly: true,
      unavailableLabel: option.source?.isActive === false ?
        "archived" :
        (isStreamMismatch ? "saved" : "no access"),
    };
  });

  return createSelectControl(buildSelectConfig({
    placeholder: multiple ? "+ Add types" : "+ Add type",
    ...config,
    name: config.name || "types",
    multiple,
  }, options));
}

/**
 * Creates a stream-filtered type select preset for Labs/Maintenance.
 * @param {object} config Preset configuration.
 * @param {HTMLElement} config.root Root element to own the rendered control.
 * @param {object} config.appData Runtime app data containing config.
 * @param {string} config.itemType Entity type, such as lab or maintenance.
 * @param {string} config.stream Immutable item stream.
 * @param {string|Array<string>} [config.value] Selected type id or ids.
 * @returns {object} Select lifecycle handle.
 */
export function createStreamScopedTypeSelectPreset(config) {
  return createTypeSelectPreset(config);
}

/**
 * Creates a date-range preset for filters and future reporting controls.
 * @param {object} config Preset configuration.
 * @param {HTMLElement} config.root Root element to own the rendered control.
 * @param {object} [config.value] Selected range as { start, end }.
 * @returns {object} Date control lifecycle handle.
 */
export function createDateRangePreset(config) {
  return createDateControl({
    startLabel: "Start date",
    endLabel: "End date",
    ...config,
    type: "range",
  });
}
